// ./src/QrScanner/scanDecode.ts
//
// What the scanner ACCEPTS as a reading — ONE copy of the rules, run by the guard on fixtures
// (ROADMAP #65 e, the decoder switch of 2026-09-29).
//
// ⚠⚠ THE DECODER IS zxing-wasm (zxing-cpp compiled to WebAssembly) ON EVERY DEVICE. The phone app
// is a PWA, so on an iPhone it runs inside WebKit, which has no `BarcodeDetector` — whatever we ship
// is all it gets. It used to ship ZXing-JS. Measured on a synthetic harness (9 symbologies × 14
// conditions, decoded through the scanner's own crop): ZXing-JS read 60 of 126, the browser's
// native detector 96, zxing-wasm with the attempts below 109 — at ~2–10ms a decode against ZXing-JS's
// ~150ms. ONE engine everywhere also means a code reads as the same text on every phone.
//
// ⚠ Pure and import-free (types only): `scripts/checkInventoryScan.ts` (apps/app) RUNS it.

/** The part of a zxing-wasm `ReadResult` these rules look at. */
export interface ScanCandidate {
  isValid: boolean;
  format: string;
  text: string;
  /** Where it is in the decoded picture: its four corners, when the reader says. */
  position?: { topLeft: ScanPoint; topRight: ScanPoint; bottomRight: ScanPoint; bottomLeft: ScanPoint } | null;
}

export interface ScanPoint {
  x: number;
  y: number;
}

const cornersOf = (r: ScanCandidate): ScanPoint[] | null =>
  r.position ? [r.position.topLeft, r.position.topRight, r.position.bottomRight, r.position.bottomLeft] : null;

const middleOf = (r: ScanCandidate): ScanPoint | null => {
  const corners = cornersOf(r);

  return corners ? { x: corners.reduce((sum, c) => sum + c.x, 0) / 4, y: corners.reduce((sum, c) => sum + c.y, 0) / 4 } : null;
};

/**
 * Is this reading part of a longer one? Where it is decides: its middle inside the other's box. Only with no position, its
 * text inside the other's. ⚠ The text alone cannot decide: a short code beside "…CODE" is a real code that happens to match.
 */
const isFragmentOf = (r: ScanCandidate, other: ScanCandidate): boolean => {
  if (other.text.length <= r.text.length) {
    return false;
  }

  const middle = middleOf(r);
  const corners = cornersOf(other);

  if (!middle || !corners) {
    return other.text.includes(r.text);
  }

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);

  return middle.x >= Math.min(...xs) && middle.x <= Math.max(...xs) && middle.y >= Math.min(...ys) && middle.y <= Math.max(...ys);
};

/**
 * Linear formats with a short payload and a weak (or no) checksum. A fragment of a longer code can
 * decode as one of these — MEASURED: a sideways UPC-A read as the EAN-8 `60002914`, a tilted ITF
 * `12345678` as `1234` — so they are accepted only once two attempts agree (`needsConfirmation`).
 */
const SHORT_LINEAR_FORMATS = new Set(["EAN8", "UPCE", "ITF", "Codabar", "DataBar", "DataBarLimited"]);

/** The shortest ITF accepted. ITF has no checksum of its own; a 2–4 digit read is almost always a fragment. */
export const ITF_MIN_LENGTH = 6;

/**
 * The one reading to act on from a decode that may return several.
 *
 * ⚠ zxing-wasm is asked for up to four symbols because a fragment and the real code can come back
 * TOGETHER (measured: `1234` beside `12345678`), and asked for one it sometimes returned the fragment.
 * So: drop invalid results and too-short ITF, then every fragment of a longer reading.
 *
 * ⚠⚠ THEN THE ONE NEAREST WHERE THE CAMERA IS AIMED (`aim`, the picture's middle — owner, 2026-11-02: it read "code right
 * close to it if there a few of codes nearby"). Two wristbands in the frame are two real codes, and the LONGER of them is
 * no more the intended one. With no `aim`, or no position, the longest, as before.
 */
export const pickBarcode = <T extends ScanCandidate>(results: readonly T[], aim?: ScanPoint | null): T | null => {
  const valid = results.filter((r) => r.isValid && r.text.trim() && !(r.format === "ITF" && r.text.length < ITF_MIN_LENGTH));
  const whole = valid.filter((r) => !valid.some((other) => other !== r && isFragmentOf(r, other)));
  const distance = (r: T): number | null => {
    const middle = middleOf(r);

    return aim && middle ? Math.hypot(middle.x - aim.x, middle.y - aim.y) : null;
  };

  return (
    [...whole].sort((a, b) => {
      const da = distance(a);
      const db = distance(b);

      return da !== null && db !== null ? da - db : b.text.length - a.text.length;
    })[0] ?? null
  );
};

/**
 * The text stored and compared for a reading.
 *
 * ⚠⚠ zxing-wasm reports a UPC-A as a 13-digit `EAN13` with a leading 0 (`0036000291452`); ZXing-JS —
 * which scanned every code stored so far — returned the 12-digit UPC-A (`036000291452`). Without this,
 * every UPC-A in the catalog would be an UNKNOWN code after the switch, and be created twice.
 */
export const normalizeScanText = (format: string | null | undefined, text: string): string => {
  const value = text.trim();

  if (format === "EAN13" && /^0\d{12}$/.test(value)) {
    return value.slice(1);
  }

  return value;
};

/** True when a reading of this format must be seen twice before it counts. */
export const needsConfirmation = (format: string | null | undefined): boolean => SHORT_LINEAR_FORMATS.has(format ?? "");

/** How long the first of two agreeing readings stays valid. */
export const CONFIRM_WINDOW_MS = 1500;

/**
 * Decide one reading against the one before it: accept it, or hold it until it is confirmed.
 * Returns the text to act on, or null — and the pending reading to carry into the next attempt.
 */
export const confirmReading = (
  reading: { format: string; text: string } | null,
  pending: { text: string; at: number } | null,
  now: number,
  /**
   * Every format must be seen twice: a code the camera merely swept past, on its way to another, is read once and never
   * acted on. The scanner always asks for this; a tick is ~150ms, so it costs about that.
   */
  everyFormat = false,
): { accept: string | null; pending: { text: string; at: number } | null } => {
  if (!reading) {
    return { accept: null, pending };
  }

  if (!everyFormat && !needsConfirmation(reading.format)) {
    return { accept: reading.text, pending: null };
  }

  if (pending && pending.text === reading.text && now - pending.at <= CONFIRM_WINDOW_MS) {
    return { accept: reading.text, pending: null };
  }

  return { accept: null, pending: { text: reading.text, at: now } };
};

/**
 * The extra attempt made on each tick, in turn, after the plain one. Every tick decodes the crop as
 * it is PLUS one of these, so a tick costs two cheap decodes however many variants exist, and all of
 * them have been tried within five ticks.
 * - `light`: the lighting-corrected crop (shadow + weak contrast — measured +2 cases);
 * - an angle: the crop straightened by that many degrees, for a code held at a slant (zxing-cpp's own
 *   rotation only tries multiples of 90° — measured +18 cases).
 */
export const ATTEMPT_CYCLE: ReadonlyArray<"light" | number> = ["light", 20, -20, 40, -40];

/**
 * A code must be out of sight this long before it can count again. ⚠ Measured from when it was LAST SEEN, not from when it
 * was accepted: a badge held in front of the camera re-fired every 1.6s (owner, 2026-11-02: "scans non-stop and rescans
 * already scanned code"). A deliberate re-scan is the same code again after moving away.
 */
export const SAME_CODE_GONE_MS = 2500;

/** After any scan, nothing else counts for this long: time to move on, and to read the result. */
export const NEXT_CODE_PAUSE_MS = 1200;

export interface ScanGate {
  text: string | null;
  seenAt: number;
  acceptedAt: number;
  /** The codes spent before this one and possibly still in view: two wristbands in the guide took turns, each with a beep. */
  earlier?: Array<{ text: string; seenAt: number }>;
}

/** How many earlier codes are remembered: what a hand can hold in a guide, not a guest list. */
const EARLIER_CODES = 4;

const stillSpent = (gate: ScanGate, text: string, now: number, goneMs: number): boolean =>
  (gate.earlier ?? []).some((e) => e.text === text && now - e.seenAt < goneMs);

const seenAgain = (gate: ScanGate, text: string, now: number): ScanGate => ({
  ...gate,
  earlier: (gate.earlier ?? []).map((e) => (e.text === text ? { ...e, seenAt: now } : e)),
});

export const OPEN_SCAN_GATE: ScanGate = { text: null, seenAt: 0, acceptedAt: 0 };

/**
 * One confirmed reading against the gate: does it count? `held`: a lookup is running, or the page has paused the camera.
 * `goneMs`: how long a spent code must be out of sight. A page where the operator confirms each scan asks for less.
 */
export const passScanGate = (
  gate: ScanGate,
  text: string,
  now: number,
  held = false,
  goneMs = SAME_CODE_GONE_MS,
): { accept: boolean; gate: ScanGate } => {
  if (gate.text === text && now - gate.seenAt < goneMs) {
    return { accept: false, gate: { ...gate, seenAt: now } };
  }

  if (stillSpent(gate, text, now, goneMs)) {
    return { accept: false, gate: seenAgain(gate, text, now) };
  }

  if (held || now - gate.acceptedAt < NEXT_CODE_PAUSE_MS) {
    return { accept: false, gate };
  }

  const earlier = [...(gate.text ? [{ text: gate.text, seenAt: gate.seenAt }] : []), ...(gate.earlier ?? [])]
    .filter((e) => e.text !== text && now - e.seenAt < goneMs)
    .slice(0, EARLIER_CODES);

  return { accept: true, gate: { text, seenAt: now, acceptedAt: now, earlier } };
};

/**
 * The page has moved on (the mode changed): no code is spent any more, the same one included (owner, 2026-11-02: "I cannot
 * scan the same code the second time until I scan something else"). ⓘ Not for finishing with a voucher: that reopened it,
 * still in view (owner, 2026-11-03). The
 * pause still runs, so the result of what they just did can be read before the next one replaces it.
 */
export const rearmScanGate = (now: number): ScanGate => ({ text: null, seenAt: 0, acceptedAt: now });

/**
 * Any sighting of a code still spent keeps it spent, even a first reading that has not been confirmed yet.
 * ⚠ Only while it is still spent: a code that has been away long enough has earned its next read, and refreshing it here, on
 * the first of the two reads it needs, would turn the second away. It could then never be read again.
 */
export const noteScanSighting = (gate: ScanGate, text: string | null | undefined, now: number, goneMs = SAME_CODE_GONE_MS): ScanGate => {
  if (!text) {
    return gate;
  }

  if (gate.text === text && now - gate.seenAt < goneMs) {
    return { ...gate, seenAt: now };
  }

  return stillSpent(gate, text, now, goneMs) ? seenAgain(gate, text, now) : gate;
};

/**
 * The camera is back after a pause (a drawer over it): what was spent counts as seen just now. ⚠ Nothing was looking while it
 * was off, so "not seen for a while" means nothing yet: a badge still in the frame re-scanned and replaced the "saved" message.
 */
export const resumeScanGate = (gate: ScanGate, now: number): ScanGate => ({
  ...gate,
  seenAt: gate.text ? now : gate.seenAt,
  earlier: (gate.earlier ?? []).map((e) => ({ ...e, seenAt: now })),
});
